import React, { useState, useEffect } from 'react';
import { Star, ShoppingCart, Heart, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useCart } from '../context/CartContext';
import { translateProductName } from '../utils/translateProductName';

interface Product {
  id: string; // UUID
  name: string;
  price: number;
  image_url: string;
  rating: number;
  num_reviews: number;
  category: string;
  description: string;
  brand?: string;
  count_in_stock?: number;
  featured?: boolean;
}

const FeaturedProducts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToCart, isUpdating } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      setLoading(true);
      try {
        console.log('🔍 Buscando produtos em destaque...');
        
        // Primeiro, tentar buscar produtos marcados como featured
        const { data: featuredData, error: featuredError } = await supabase
          .from('products')
          .select('*')
          .eq('featured', true)
          .limit(4);

        if (featuredError) {
          console.log('⚠️ Campo "featured" não existe ainda, buscando primeiros produtos:', featuredError.message);
          
          // Se o campo featured não existir, buscar os 4 primeiros produtos por rating
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('products')
            .select('*')
            .order('rating', { ascending: false })
            .limit(4);

          if (fallbackError) {
            console.error('❌ Erro ao buscar produtos fallback:', fallbackError.message);
            setProducts([]);
          } else if (fallbackData && fallbackData.length > 0) {
            console.log(`✅ ${fallbackData.length} produtos carregados (fallback)`);
            setProducts(fallbackData);
          } else {
            console.log('📦 Nenhum produto encontrado');
            setProducts([]);
          }
        } else if (featuredData && featuredData.length > 0) {
          console.log(`⭐ ${featuredData.length} produtos em destaque carregados`);
          setProducts(featuredData);
        } else {
          console.log('📦 Nenhum produto em destaque, buscando produtos por rating...');
          
          // Se não há produtos featured, buscar por rating como fallback
          const { data: ratingData, error: ratingError } = await supabase
            .from('products')
            .select('*')
            .order('rating', { ascending: false })
            .limit(4);

          if (!ratingError && ratingData) {
            console.log(`✅ ${ratingData.length} produtos carregados por rating`);
            setProducts(ratingData);
          } else {
            setProducts([]);
          }
        }
      } catch (err) {
        console.error('❌ Erro ao carregar produtos em destaque:', err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  const handleAddToCart = async (productId: string) => {
    try {
      await addToCart(productId, 1);
      console.log('✅ Produto adicionado ao carrinho:', productId);
    } catch (error) {
      console.error('❌ Erro ao adicionar produto:', error);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('products.featured')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('products.featuredDesc')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-lg p-6 animate-pulse">
                <div className="bg-gray-300 h-64 rounded mb-4"></div>
                <div className="bg-gray-300 h-4 rounded mb-2"></div>
                <div className="bg-gray-300 h-4 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Se não há produtos, mostrar uma mensagem
  if (products.length === 0) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('products.featured')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('products.featuredDesc')}
            </p>
          </div>
          <div className="text-center py-12">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto">
              <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('products.noFeatured')}
              </h3>
              <p className="text-gray-600">
                {t('products.configureAdmin')}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('products.featured')}
            <span className="bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] bg-clip-text text-transparent">
              {' '}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('products.featuredDesc')}
          </p>
          {/* Indicador de produtos em destaque */}
          <div className="flex justify-center items-center mt-4">
            <Star className="w-5 h-5 text-yellow-500 mr-2" />
            <span className="text-sm text-gray-600">
              {products.length} {t('products.featured')}
            </span>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {products.map((product) => (
            <div
              key={product.id}
              className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group overflow-hidden relative ${product.count_in_stock === 0 ? "opacity-75" : ""}`}
            >
              {/* Featured Badge */}
              {product.featured && (
                <div className="absolute top-3 left-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold z-10 flex items-center">
                  <Star className="w-3 h-3 mr-1 fill-current" />
                  {t('products.featured')}
                </div>
              )}

              {/* Product Image */}
              <div className="relative overflow-hidden">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className={`w-full h-64 object-cover transition-transform duration-300 ${product.count_in_stock === 0 ? "grayscale opacity-60" : "group-hover:scale-105"}`}
                />

                {/* Badge ESGOTADO */}
                {product.count_in_stock === 0 && (
                  <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-2 rounded-lg font-bold text-sm z-10 border-2 border-white">
                    {t("products.outOfStock")}
                  </div>
                )}
                {/* Action Buttons */}
                <div className="absolute top-3 right-3 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                    <Heart className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                    <Eye className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                  {translateProductName(product.name)}
                </h3>

                {/* Short Description */}
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {translateProductName(product.description)}
                </p>

                {/* Rating */}
                <div className="flex items-center mb-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating || 0)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600 ml-2">
                    {product.rating || 0} ({product.num_reviews || 0} {t('products.reviews')})
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-[#4ECDC4]">
                      CHF {product.price}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {/* Ver Mais Button */}
                  <button
                    onClick={() => navigate(`/producto/${product.id}`)}
                    className="flex-1 py-2.5 px-3 rounded-xl font-semibold text-sm transition-all duration-200 inline-flex items-center justify-center gap-1.5 bg-white border-2 border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4] hover:text-white hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <Eye className="w-4 h-4 flex-shrink-0" />
                    <span className="leading-none">{t('products.viewMore')}</span>
                  </button>

                  {/* Add to Cart Button */}
                  <button
                    onClick={() => handleAddToCart(product.id)}
                    disabled={isUpdating || product.count_in_stock === 0}
                    className={`flex-1 py-2.5 px-3 rounded-xl font-semibold text-sm transition-all duration-200 inline-flex items-center justify-center gap-1.5 ${product.count_in_stock === 0 ? "bg-gray-400 text-gray-700 cursor-not-allowed opacity-60" : "bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white hover:shadow-lg transform hover:-translate-y-0.5"} ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <ShoppingCart className="w-4 h-4 flex-shrink-0" />
                    <span className="leading-none">{product.count_in_stock === 0 ? t("products.outOfStock") : isUpdating ? t("products.adding") : t("products.addToCart")}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <a
            href="/produtos"
            className="border-2 border-[#4ECDC4] text-[#4ECDC4] px-8 py-3 rounded-full font-bold text-lg hover:bg-[#4ECDC4] hover:text-white transition-all duration-200 inline-block"
          >
            {t('products.viewAll')}
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
